// dates.ts — pure date helpers, ported from the prototype's data.jsx.
// TODAY uses the real current date for live API mode.

export const TODAY = new Date(
  new Date().getFullYear(),
  new Date().getMonth(),
  new Date().getDate(),
);

export function parseDate(d: string): Date {
  if (!d) return new Date(NaN);
  // ISO format: yyyy-mm-dd or yyyy-mm-ddT...
  if (d.includes('-')) {
    const [yy, mm, dd] = d.split('T')[0].split('-').map(Number);
    return new Date(yy, mm - 1, dd);
  }
  // UK format: dd/mm/yyyy
  const [dd, mm, yy] = d.split('/').map(Number);
  return new Date(yy, mm - 1, dd);
}

export function fmtDate(dt: Date): string {
  const d = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}/${dt.getFullYear()}`;
}

export function daysUntil(d: string): number {
  return Math.round((parseDate(d).getTime() - TODAY.getTime()) / 86400000);
}

export function fmtDateTime(dt: Date): string {
  return `${fmtDate(dt)} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

// Used only by the MSW mock layer to stamp writes. Anchored to the REAL
// current date (like the relative seeds) so mock-created records never sit
// outside the seeded timeline as the calendar moves on.
export function nowStamp(): string {
  return fmtDateTime(new Date());
}

export type Tone = 'success' | 'warning' | 'error' | 'info';

export interface ExpiryState {
  key: 'expired' | 'exp30' | 'exp60' | 'exp90' | 'ok';
  label: string;
  tone: Tone;
  days: number;
}

// expiry → semantic state
export function expiryState(exp: string): ExpiryState {
  const d = daysUntil(exp);
  if (d < 0) return { key: 'expired', label: 'Expired', tone: 'error', days: d };
  if (d <= 30) return { key: 'exp30', label: `${d}d left`, tone: 'error', days: d };
  if (d <= 60) return { key: 'exp60', label: `${d}d left`, tone: 'warning', days: d };
  if (d <= 90) return { key: 'exp90', label: `${d}d left`, tone: 'warning', days: d };
  return { key: 'ok', label: 'OK', tone: 'success', days: d };
}

/** Bucket date strings (dd/mm/yyyy or ISO) into daily counts for the trailing
 *  `days` window, anchored to the most recent date present (most recent last).
 *  Anchoring to the data keeps mock (fixed TODAY) and real modes meaningful. */
export function dailyCounts(dates: (string | null | undefined)[], days = 7): number[] {
  const stamps = dates
    .map((d) => {
      if (!d) return NaN;
      const iso = /^\d{4}-\d{2}-\d{2}/.test(d) ? new Date(d) : parseDate(d.split(' ')[0]);
      return iso.getTime();
    })
    .filter((t) => !Number.isNaN(t));
  if (!stamps.length) return [];
  const anchor = Math.max(...stamps);
  const day = 86400000;
  const counts = new Array(days).fill(0);
  for (const t of stamps) {
    const offset = Math.floor((anchor - t) / day);
    if (offset >= 0 && offset < days) counts[days - 1 - offset] += 1;
  }
  return counts;
}
